import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faFlask,
  faPause,
  faPlay,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";

import Button from "ui/Button";
import ModalContainer from "ui/ModalContainer";
import PanelItem from "ui/PanelItem";

import { OrganizationIntegrationsQuery } from "../../gql/Query.generated";
import {
  EndpointHealthItems,
  Notice,
  NoticeMessage,
  RetryAction,
} from "../../Webhooks/EndpointDetail";
import {
  DeliveryDetails,
  DeliveryLog,
  DeliveryType,
} from "../../Webhooks/EndpointDetail/Deliveries";
import { TestOutcome, testFailureMessage } from "../../Webhooks/WebhookForm";
import {
  useDeleteIncidentIoEndpointMutation,
  useTestIncidentIoEndpointMutation,
  useUpdateIncidentIoEndpointMutation,
} from "../gql/Mutation.generated";
import IncidentIoForm from "../IncidentIoForm";

type IncidentIoEndpointType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["incidentIoEndpoints"][number];

type View =
  | { kind: "overview" }
  | { kind: "edit" }
  | { kind: "delivery"; delivery: DeliveryType };

const REFETCH_QUERIES = ["OrganizationIntegrations"];

function IncidentIoEndpointModal({
  organizationSlug,
  endpoint,
  canEdit,
  onClose,
  onRemoved,
}: {
  organizationSlug: string;
  endpoint: IncidentIoEndpointType;
  canEdit: boolean;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const [view, setView] = useState<View>({ kind: "overview" });
  const [notice, setNotice] = useState<Notice | null>(null);
  const showOverview = () => setView({ kind: "overview" });

  return (
    <ModalContainer
      title={
        view.kind === "edit"
          ? `Edit incident.io Alert Source: ${endpoint.name}`
          : `incident.io: ${endpoint.name}`
      }
      onClose={onClose}
    >
      {view.kind === "edit" ? (
        <div className="max-w-[640px] py-3">
          <IncidentIoForm
            organizationSlug={organizationSlug}
            endpoint={endpoint}
            onSaved={(message) => {
              setNotice({ kind: "success", message });
              showOverview();
            }}
            onCancel={showOverview}
          />
        </div>
      ) : view.kind === "delivery" ? (
        <div className="py-3">
          <Button bare variant="link" className="mb-3" onClick={showOverview}>
            &lsaquo; Back to all deliveries
          </Button>
          <DeliveryDetails
            kind="incident_io"
            delivery={view.delivery}
            actions={
              canEdit && (
                <RetryAction
                  kind="incident_io"
                  endpoint={endpoint}
                  delivery={view.delivery}
                  onRetried={(delivery) =>
                    setView({ kind: "delivery", delivery })
                  }
                />
              )
            }
          />
        </div>
      ) : (
        <div className="py-3">
          {notice && <NoticeMessage notice={notice} />}
          <PanelItem label="URL">
            <span className="break-all font-query text-[13px]">
              {endpoint.url}
            </span>
          </PanelItem>
          <EndpointHealthItems
            organizationSlug={organizationSlug}
            endpoint={endpoint}
          />
          {canEdit && (
            <EndpointActions
              organizationSlug={organizationSlug}
              endpoint={endpoint}
              onEdit={() => {
                setNotice(null);
                setView({ kind: "edit" });
              }}
              onNotice={setNotice}
              onRemoved={onRemoved}
            />
          )}
          <DeliveryLog
            organizationSlug={organizationSlug}
            kind="incident_io"
            endpointId={endpoint.id}
            onSelect={(delivery) => setView({ kind: "delivery", delivery })}
          />
        </div>
      )}
    </ModalContainer>
  );
}

function EndpointActions({
  organizationSlug,
  endpoint,
  onEdit,
  onNotice,
  onRemoved,
}: {
  organizationSlug: string;
  endpoint: IncidentIoEndpointType;
  onEdit: () => void;
  onNotice: (notice: Notice | null) => void;
  onRemoved: () => void;
}) {
  const reportError = (message: string) => onNotice({ kind: "error", message });
  const [testEndpoint, testResult] = useTestIncidentIoEndpointMutation({
    variables: { organizationSlug, incidentIoEndpointId: endpoint.id },
    onError: (error) => reportError(error.message),
  });
  // The result carries the changed fields, so the cached endpoint updates without
  // a refetch
  const [updateEndpoint, updateResult] = useUpdateIncidentIoEndpointMutation({
    onError: (error) => reportError(error.message),
  });
  const [deleteEndpoint, deleteResult] = useDeleteIncidentIoEndpointMutation({
    refetchQueries: REFETCH_QUERIES,
    onCompleted: onRemoved,
    onError: (error) => reportError(error.message),
  });
  const busy =
    testResult.loading || updateResult.loading || deleteResult.loading;

  // Reports a failed test, keeping the response body since it often says why
  // incident.io rejected the alert
  const runTest = async () => {
    const tested = await testEndpoint();
    const result = tested.data?.testIncidentIoEndpoint;
    if (result && result.status !== "succeeded") {
      onNotice({
        kind: "error",
        message: (
          <TestOutcome result={result}>
            Test alert failed: {testFailureMessage(result)}
          </TestOutcome>
        ),
      });
      return null;
    }
    return result ?? null;
  };

  const sendTest = async () => {
    const result = await runTest();
    if (result) {
      onNotice({
        kind: "success",
        message: `Test alert sent (HTTP ${result.responseStatus}). Resolve it in incident.io.`,
      });
    }
  };

  const enable = async () => {
    const result = await runTest();
    if (!result) {
      return;
    }
    const updated = await updateEndpoint({
      variables: { incidentIoEndpointId: endpoint.id, enabled: true },
      onError: (error) =>
        reportError(
          `Test alert sent, but enabling the alert source failed: ${error.message}`,
        ),
    });
    if (updated.data) {
      onNotice({
        kind: "success",
        message: `Test alert sent (HTTP ${result.responseStatus}), alert source enabled. Resolve the test alert in incident.io.`,
      });
    }
  };

  const disable = async () => {
    const updated = await updateEndpoint({
      variables: { incidentIoEndpointId: endpoint.id, enabled: false },
    });
    if (updated.data) {
      onNotice({ kind: "success", message: "Alert source disabled" });
    }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
      <Button
        bare
        variant="link"
        disabled={busy}
        onClick={() => {
          onNotice(null);
          void sendTest();
        }}
      >
        <FontAwesomeIcon icon={faFlask} /> Send test alert
      </Button>
      <Button bare variant="link" disabled={busy} onClick={onEdit}>
        <FontAwesomeIcon icon={faEdit} /> Edit
      </Button>
      <Button
        bare
        variant="link"
        disabled={busy}
        onClick={() => {
          onNotice(null);
          void (endpoint.disabledAt ? enable() : disable());
        }}
      >
        {endpoint.disabledAt ? (
          <>
            <FontAwesomeIcon icon={faPlay} /> Test and enable
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faPause} /> Disable
          </>
        )}
      </Button>
      <Button
        bare
        variant="danger"
        disabled={busy}
        onClick={() => {
          if (
            window.confirm(
              `Remove the incident.io alert source "${endpoint.name}"?`,
            )
          ) {
            onNotice(null);
            void deleteEndpoint({
              variables: { incidentIoEndpointId: endpoint.id },
            });
          }
        }}
      >
        <FontAwesomeIcon icon={faTrashAlt} /> Remove
      </Button>
    </div>
  );
}

export default IncidentIoEndpointModal;
